// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { PaginationState, SortingState } from "@tanstack/react-table";
import { Loader2, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useChainRegionsQuery, useDeleteChainRegionMutation, useEditChainRegionMutation } from "@/api/hooks/use-chains";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { downloadCsv } from "@/lib/csv";
import { getDefaultPageSize } from "@/lib/preferences";

interface RegionRow {
  regionName: string;
  storesCount: number;
}

interface ChainRegionsTabProps {
  chainName: string;
}

export function ChainRegionsTab({ chainName }: ChainRegionsTabProps) {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.chainsRegionsEdit);
  const { data, isLoading, refetch, isFetching } = useChainRegionsQuery(chainName);
  const editRegion = useEditChainRegionMutation(chainName);
  const deleteRegion = useDeleteChainRegionMutation(chainName);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getDefaultPageSize(),
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: "regionName", desc: false }]);

  const [editingRegion, setEditingRegion] = useState<RegionRow | null>(null);
  const [editName, setEditName] = useState("");
  const [deletingRegion, setDeletingRegion] = useState<RegionRow | null>(null);

  const handleEditOpen = useCallback(function handleEditOpen(region: RegionRow) {
    setEditingRegion(region);
    setEditName(region.regionName);
  }, []);

  async function handleEditSave() {
    if (!editingRegion) return;
    try {
      await editRegion.mutateAsync({
        oldRegionName: editingRegion.regionName,
        newRegionName: editName.trim(),
      });
      toast.success(`Region renamed to "${editName.trim()}"`);
      setEditingRegion(null);
    } catch {
      // Error toast handled by API middleware
    }
  }

  async function handleDelete() {
    if (!deletingRegion) return;
    try {
      await deleteRegion.mutateAsync(deletingRegion.regionName);
      toast.success(`Region "${deletingRegion.regionName}" deleted`);
      setDeletingRegion(null);
    } catch {
      // Error toast handled by API middleware
    }
  }

  const columns = useMemo<DataTableColumnDef<RegionRow>[]>(
    () => [
      {
        accessorKey: "regionName",
        header: t("chains.regions.columns.regionName"),
        enableSorting: true,
        enableHiding: false,
        meta: { columnLabel: t("chains.regions.columns.regionName"), className: "pl-4", sticky: "left" },
        cell: ({ row }) => row.original.regionName,
      },
      {
        accessorKey: "storesCount",
        header: t("chains.regions.columns.storesCount"),
        enableSorting: true,
        meta: { columnLabel: t("chains.regions.columns.storesCount") },
      },
      {
        id: "actions",
        size: 50,
        meta: { forceWidth: true, sticky: "right" },
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) =>
          canEdit ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
                <DropdownMenuItem onSelect={() => handleEditOpen(row.original)}>
                  <Pencil className="h-4 w-4" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setDeletingRegion(row.original)} className="text-destructive">
                  <Trash2 className="h-4 w-4" />
                  Remove
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null,
      },
    ],
    [t, handleEditOpen, canEdit],
  );

  const handleExport = useCallback(async () => {
    const regions = data?.regions ?? [];
    downloadCsv(
      `chain-${chainName}-regions.csv`,
      ["Region Name", "Stores"],
      regions.map((r) => [r.regionName, String(r.storesCount)]),
    );
  }, [data, chainName]);

  const allRegions = data?.regions ?? [];

  // Client-side sort
  const sortKey = sorting[0]?.id as keyof RegionRow | undefined;
  const sortDesc = sorting[0]?.desc ?? false;
  const sorted = useMemo(() => {
    if (!sortKey) return allRegions;
    return [...allRegions].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sortDesc ? -cmp : cmp;
    });
  }, [allRegions, sortKey, sortDesc]);

  // Client-side paginate
  const paginated = useMemo(
    () => sorted.slice(pagination.pageIndex * pagination.pageSize, (pagination.pageIndex + 1) * pagination.pageSize),
    [sorted, pagination.pageIndex, pagination.pageSize],
  );

  return (
    <div className="p-6">
      <DataTable
        storageKey="chain-regions"
        columns={columns}
        data={paginated}
        totalCount={allRegions.length}
        isLoading={isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        onRefresh={refetch}
        isRefreshing={isFetching}
        onExport={handleExport}
      />

      {/* Edit Region Dialog */}
      <Dialog open={!!editingRegion} onOpenChange={() => setEditingRegion(null)}>
        <DialogContent id="dialog-rename-region" className="sm:max-w-[33rem] p-0 gap-0 overflow-hidden flex flex-col">
          <div className="flex flex-row">
            <WizardPane icon={Pencil} />
            <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
              <DialogHeader>
                <DialogTitle>Rename Region</DialogTitle>
              </DialogHeader>
              <div className="space-y-2">
                <Label>Region Name</Label>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />
              </div>
            </div>
          </div>
          <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
            <Button variant="outline" onClick={() => setEditingRegion(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleEditSave}
              disabled={!canEdit || editRegion.isPending || editName.trim() === editingRegion?.regionName}
            >
              {editRegion.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />}
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Region Dialog */}
      <Dialog open={!!deletingRegion} onOpenChange={() => setDeletingRegion(null)}>
        <DialogContent id="dialog-remove-region" className="sm:max-w-[33rem] p-0 gap-0 overflow-hidden flex flex-col">
          <div className="flex flex-row">
            <WizardPane variant="destructive" icon={Trash2} />
            <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
              <DialogHeader>
                <DialogTitle>Remove Region</DialogTitle>
                <DialogDescription>
                  Are you sure you want to remove region &quot;
                  {deletingRegion?.regionName}&quot;?
                  {deletingRegion && deletingRegion.storesCount > 0 && (
                    <span className="mt-2 block text-destructive">
                      This region has {deletingRegion.storesCount} stores assigned. This action cannot be undone.
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
          <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
            <Button variant="outline" onClick={() => setDeletingRegion(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={!canEdit || deleteRegion.isPending}>
              {deleteRegion.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
